<template>
  <div>
    <div class="grid--three-column">
      <div v-if="carrier.contact" class="card mb-4">
        <h5 class="card-header">Contact</h5>
        <div class="card-body">
          <div class="mb-2">
            <span class="h5"><i class="fa-lg fas fa-info mr-1 ml-2"></i> Status: </span>
            <span :class="carrier.isActive ? 'text-success' : 'text-danger'">{{ carrier.isActive ? 'Active' : 'Inactive' }}</span>
          </div>
          <div class="mb-2">
            <span class="h5"><i class="fa-lg fas fa-paper-plane mr-1"></i>Can Dispatch:</span>
            <span :class="carrier.canDispatch ? 'text-success' : 'text-danger'">{{ carrier.canDispatch ? 'Active' : 'Inactive' }}</span>
          </div>
          <div v-if="claimStats.openCount > 0" class="mb-2">
            <span class="h5"><i class="fa-lg fas fa-exclamation-triangle mr-1"></i>Open claims:</span>
            <span class="claimBadge"><b>{{  claimStats.openCount }} open </b></span>
            <span class="ml-1 red-text">{{ String(parseFloat(claimStats.pendingAmount)) | formatMoney }} pending</span>
          </div>
          <div class="mb-2">
            <b-rating no-border disabled :value="carrier.rating" class="col-xl-6 col-12" />
          </div>
          <LabeledData label="Company" :value="carrier.contact.company" hide-if-blank />
          <LabeledData label="Contact" :value="carrier.contact.contact" hide-if-blank />
          <LabeledData label="Phone" :value="carrier.contact.phone | formatPhone" :externalLink="'tel:' + carrier.contact.phone" hide-if-blank />
          <LabeledData label="Phone 2" :value="carrier.contact.phone2 | formatPhone" :externalLink="'tel:' + carrier.contact.phone2" hide-if-blank />
          <LabeledData label="Email" :value="carrier.contact.email" :externalLink="'mailto:' + carrier.contact.email" hide-if-blank />
          <LabeledData label="Cell phone" :value="carrier.contact.cellPhone | formatPhone" :externalLink="'tel:' + carrier.contact.cellPhone" hide-if-blank />
          <LabeledData label="Fax" :value="carrier.contact.fax | formatPhone" hide-if-blank />
          <LabeledData label="Tax Id Num" :value="carrier.taxIdNum" hide-if-blank />
          <LabeledData label="MC Number" :value="carrier.mcNumber" hide-if-blank />
          <LabeledData label="USDOT Number" :value="carrier.usdot" hide-if-blank />
          <LabeledData label="Central Dispatch user" :value="carrier.cdGuid ? 'Yes': 'No'" hide-if-blank />
          <LabeledData label="Private Marketplace user" :value="carrier.cdMarketplaceGuid ? 'Yes': 'No'" hide-if-blank />
        </div>
      </div>
      <div v-if="carrier.location" class="card mb-4">
        <h5 class="card-header">Address</h5>
        <div class="card-body">
          <LabeledData label="Location" :value="carrier.location.city + ', ' + carrier.location.state + ' ' + carrier.location.zip" hide-if-blank />
          <LabeledData label="Address" :value="carrier.location.address" hide-if-blank />
          <LabeledData label="Address2" :value="carrier.location.address2" hide-if-blank />
          <div class="mt-3">
            <span class="h5"><i class="fa-lg fas fa-comments mr-1"></i> Notes:</span>
            <div class="pre">{{ carrier.notes }}</div>
          </div>
        </div>
      </div>
      <div class="card mb-4">
        <h5 class="card-header">FMCSA Verification checklist</h5>
        <div class="card-body">
          <div v-if="fmcsaResponse && fmcsaResponse.authority">
            <div v-if="showNameMismatchWarning"
              class="alert alert-warning d-flex"
            >
              <i class="fas fa-exclamation-triangle mr-2 mt-1" style="color: orange;"></i>
              <div>
                <strong>Warning:</strong> FMCSA Legal Name ({{ fmcsaResponse.carrier.legalName }}) does not match CRM Company Name ({{ carrier.contact.company }}).
              </div>
            </div>

            <FmcsaChecklistItem label="Allowed to Operate" :value="fmcsaResponse.carrier.allowedToOperate === 'Y' ? 'Yes' : 'No'" />
            <FmcsaChecklistItem label="USDOT#" :value="fmcsaResponse.carrier.dotNumber" />
            <FmcsaChecklistItem label="MC Number" :value="mcNumber" />
            <FmcsaChecklistItem label="Business Type" value="Carrier" />
            <FmcsaChecklistItem label="Carrier Operations" :value="carrierOperationDesc || 'N/A'" />
            <FmcsaChecklistItem label="MCS 150 Outdated" :value="fmcsaResponse.carrier.mcs150Outdated === 'Y' ? 'Yes' : 'No'" />

            <FmcsaChecklistItem
              v-if="fmcsaResponse.carrier.oosDate"
              label="Out of Service Date"
              :value="fmcsaResponse.carrier.oosDate"
            />

            <AuthorityBadge
              v-if="fmcsaResponse.authority"
              label="Authority Status"
              :broker="fmcsaResponse.authority.brokerAuthorityStatus"
              :common="fmcsaResponse.authority.commonAuthorityStatus"
              :contract="fmcsaResponse.authority.contractAuthorityStatus"
              class="d-none"
            />
          </div>
          <div v-else-if="fmcsaResponse && !fmcsaResponse.authority">
            <a
              v-if="fmcsaResponse.authority===null"
              class="badge badge-secondary px-4 py-2"
              href="https://safer.fmcsa.dot.gov/CompanySnapshot.aspx"
              target="_blank"
            >
              Check FMCSA database
            </a>
          </div>
          <div v-if="!fmcsaResponse && fmcsaErrorMsg" class="alert alert-warning d-flex">
            <i class="fas fa-exclamation-triangle mr-2" style="color: orange; margin-top: 2px;"></i>
            <div>
              <strong>Warning:</strong> {{fmcsaErrorMsg}}
            </div>
          </div>
        </div>
      </div>
      <CardComponent>
        <template v-slot:header>
          Routes
        </template>

        <template>
          {{ carrier.routes }}
        </template>
      </CardComponent>
      <div class="card mb-4">
        <h5 class="card-header">Financial Summary</h5>
        <div class="card-body">
          <LabeledData label="Pending Invoices" :value="carrier.pendingInvoices" />
          <LabeledData label="Pending Balance" :value="carrier.pendingBalance"/>
        </div>
      </div>
    </div>
    <div class="card mb-4">
      <div class="card-body">
        <Notes :carrierId="carrier.id"/>
      </div>
    </div>
    <div v-if="carrier.leads.length" class="card mb-4">
      <h5 class="card-header">Active Orders</h5>
      <div class="card-body">
        <table class="table">
          <thead class="table">
            <tr>
              <th scope="col">Id</th>
              <th scope="col">Pickup Date</th>
              <th scope="col">Customer</th>
              <th scope="col">Vehicles</th>
              <th scope="col">Route</th>
              <th scope="col">Carrier Pay</th>
              <th scope="col">User</th>
              <th scope="col">Status</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="lqo in carrier.leads" :key="lqo.id">
              <td class="px-1">
                <router-link :to="{ name: 'OrderDetails', params: { id: lqo.id } }">#{{ lqo.id }}</router-link>
              </td>
              <td>{{ lqo.pickupDate | formatDate }}</td>
              <td>
                <div v-if="lqo.customer.contact.firstName || lqo.customer.contact.lastName">{{ lqo.customer.contact.firstName }} {{ lqo.customer.contact.lastName }}</div>
                <div v-if="lqo.customer.contact.company">{{ lqo.customer.contact.company }}</div>
              </td>
              <td>
                <div v-for="vehicle in lqo.vehicles" :key="vehicle.id">{{ vehicle.year }} {{ vehicle.make }} {{ vehicle.model }}</div>
              </td>
              <td>
                <div class="route">
                  <LeadlikeTableLocation :location="lqo.pickupLocation" />
                  <span class="fas fa-arrow-right"></span>
                  <LeadlikeTableLocation :location="lqo.deliveryLocation" />
                </div>
              </td>
              <td class="text-right">{{ lqo.carrierPay | formatMoney }}</td>
              <td>{{ lqo.user ? lqo.user.name : '-' }}</td>
              <td>{{ lqo.leadStatus.title }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import { ApiClient } from '@/api/ApiClient';
import LabeledData from '@/components/shared/LabeledData';
import LeadlikeTableLocation from '@/components/shared/LeadlikeTableLocation';
import CardComponent from '@/components/shared/CardComponent.vue';
import Notes from './Notes.vue';
import FmcsaChecklistItem from './FmcsaChecklistItem.vue';
import AuthorityBadge from './AuthorityBadge.vue';

export default {
  name: 'CarrierDetails',
  components: { LabeledData, LeadlikeTableLocation, Notes, CardComponent,
    FmcsaChecklistItem,
    AuthorityBadge
     },
  async mounted() {
    await this.fetch();
  },
  props: {
    carrier: {
      type: Object,
      default: () => ({}),
    },
  },
  data(){
    return {
      fmcsaResponse: null,
      fmcsaErrorMsg: '',
      claimStats: null,
    }
  },
  methods: {
    async fetch() {
      const apiResponse = await ApiClient.carriers.fmcsa({ id: this.carrier.id });
      if (apiResponse.status === 200) {
        this.fmcsaResponse = apiResponse.data.data;
      }
      if (apiResponse.status === 400) {
        console.log(apiResponse)
        this.fmcsaErrorMsg = apiResponse.data.message
      }

      const claimStatsResponse = await ApiClient.claimStats.get({ carrier: this.carrier.id });
      if (claimStatsResponse.status === 200) {
        this.claimStats = claimStatsResponse.data.data;
      }
    }
  },
  computed: {
    carrierOperationDesc() {
      return this.fmcsaResponse?.carrier?.carrierOperation?.carrierOperationDesc || '';
    },
    mcNumber() {
      if (this.fmcsaResponse && this.fmcsaResponse.authority && this.fmcsaResponse.authority.docketNumber) {
        return this.fmcsaResponse.authority.docketNumber;
      }
      return this.carrier.mcNumber;
    },
    showNameMismatchWarning() {
      const crm = this.carrier.contact && this.carrier.contact.company;
      const legal = this.fmcsaResponse?.carrier?.legalName;
      if (!crm || !legal) return false;
      return crm.toLowerCase() !== legal.toLowerCase();
    }
  },
};
</script>

<style lang="scss" scoped>
.grid--three-column {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-template-rows: 1fr;
  grid-column-gap: 16px;
  grid-row-gap: 0px;
}
.pre {
  white-space: pre-wrap;
}
.route {
  display: grid;
  grid-template-columns: 1fr 24px 1fr;
  align-items: center;
}
.claimBadge{
  display: inline-block;
  padding: 2px 16px;
  color: #C31813;
  background-color: #F5DEE8;
  border-radius: 100px;
  border: 1px solid transparent;
  border-color: #F1BABC;
}
.dark-style .claimBadge{
  color: #F86F6B;
  background-color: #4F3748;
  border-color: #A34B48;
}
</style>
